/**
 * Copyright (c) 2018-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { __assign } from "tslib";
import { Segmentation, SortedArray } from '../../../../mol-data/int';
import { combinations } from '../../../../mol-data/util/combination';
import { IntAdjacencyGraph } from '../../../../mol-math/graph';
import { Vec3 } from '../../../../mol-math/linear-algebra';
import { PrincipalAxes } from '../../../../mol-math/linear-algebra/matrix/principal-axes';
import { fillSerial, arraySetAdd } from '../../../../mol-util/array';
import { ElementSymbol, BondType } from '../../model/types';
import { getPositions } from '../../util';
import { Unit } from '../unit';
import { EmptyCarbohydrates } from './data';
import { UnitRings, UnitRing } from '../unit/rings';
import { cantorPairing } from '../../../../mol-data/util';
var C = ElementSymbol('C'), O = ElementSymbol('O');
var SugarRingFps = [
    UnitRing.elementFingerprint([C, C, C, O]),
    UnitRing.elementFingerprint([C, C, C, C, O]),
    UnitRing.elementFingerprint([C, C, C, C, C, O]),
    UnitRing.elementFingerprint([C, C, C, C, C, C, O]),
];
function getAnomericCarbon(unit, ringAtoms) {
    var indexHasTwoOxygen = -1, indexHasOxygenAndCarbon = -1, indexHasC1Name = -1, indexIsCarbon = -1;
    var elements = unit.elements;
    var _a = unit.model.atomicHierarchy.atoms, type_symbol = _a.type_symbol, label_atom_id = _a.label_atom_id;
    var _b = unit.bonds, neighbor = _b.b, offset = _b.offset;
    for (var i = 0, il = ringAtoms.length; i < il; ++i) {
        var ei = elements[ringAtoms[i]];
        if (type_symbol.value(ei) !== C)
            continue;
        var linkedOxygenCount = 0;
        var linkedCarbonCount = 0;
        for (var j = offset[ringAtoms[i]], jl = offset[ringAtoms[i] + 1]; j < jl; ++j) {
            var ej = elements[neighbor[j]];
            var typeSymbol = type_symbol.value(ej);
            if (typeSymbol === O)
                ++linkedOxygenCount;
            else if (typeSymbol === C)
                ++linkedCarbonCount;
        }
        if (linkedOxygenCount === 2) {
            // found anomeric carbon
            indexHasTwoOxygen = ei;
            break;
        }
        else if (linkedOxygenCount === 1 && linkedCarbonCount === 1) {
            // possibly an anomeric carbon if this is a mono-saccharide without a glycosidic bond
            indexHasOxygenAndCarbon = ei;
        }
        else if (label_atom_id.value(ei).startsWith('C1')) {
            // likely the anomeric carbon as it is named C1 by convention
            indexHasC1Name = ei;
        }
        else {
            // use any carbon as a fallback
            indexIsCarbon = ei;
        }
    }
    return (indexHasTwoOxygen !== -1 ? indexHasTwoOxygen
        : indexHasOxygenAndCarbon !== -1 ? indexHasOxygenAndCarbon
            : indexHasC1Name !== -1 ? indexHasC1Name
                : indexIsCarbon !== -1 ? indexIsCarbon
                    : elements[ringAtoms[0]]);
}
function getAltId(unit, index) {
    var elements = unit.elements;
    var label_alt_id = unit.model.atomicHierarchy.atoms.label_alt_id;
    return label_alt_id.value(elements[index]);
}
function getDirection(direction, unit, index, center) {
    var position = unit.conformation.position;
    Vec3.normalize(direction, Vec3.sub(direction, center, position(index, direction)));
    return direction;
}
function getAtomId(unit, index) {
    var elements = unit.elements;
    var label_atom_id = unit.model.atomicHierarchy.atoms.label_atom_id;
    return label_atom_id.value(elements[index]);
}
function filterFusedRings(unitRings, rings) {
    if (!rings || !rings.length)
        return;
    var unit = unitRings.unit, all = unitRings.all;
    var fusedRings = new Set();
    var ringCombinations = combinations(fillSerial(new Array(rings.length)), 2);
    for (var i = 0, il = ringCombinations.length; i < il; ++i) {
        var rc = ringCombinations[i];
        var r0 = all[rings[rc[0]]], r1 = all[rings[rc[1]]];
        if (SortedArray.areIntersecting(r0, r1) &&
            UnitRing.getAltId(unit, r0) === UnitRing.getAltId(unit, r1)) {
            fusedRings.add(rings[rc[0]]);
            fusedRings.add(rings[rc[1]]);
        }
    }
    if (fusedRings.size) {
        var filteredRings = [];
        for (var i = 0, il = rings.length; i < il; ++i) {
            if (!fusedRings.has(rings[i]))
                filteredRings.push(rings[i]);
        }
        return filteredRings;
    }
    else {
        return rings;
    }
}
function getSaccharideComp(compId, model) {
    return model.properties.saccharideComponentMap.get(compId);
}
export function computeCarbohydrates(structure) {
    // skip computation if there are no saccharide components in any model
    if (structure.models.reduce(function (a, v) { return a + v.properties.saccharideComponentMap.size; }, 0) === 0)
        return EmptyCarbohydrates;
    var links = [];
    var terminalLinks = [];
    var elements = [];
    var partialElements = [];
    var elementsWithRingMap = new Map();
    function ringElementKey(residueIndex, unitId, altId) {
        return residueIndex + "|" + unitId + "|" + altId;
    }
    function addRingElement(key, elementIndex) {
        if (elementsWithRingMap.has(key))
            elementsWithRingMap.get(key).push(elementIndex);
        else
            elementsWithRingMap.set(key, [elementIndex]);
    }
    function fixLinkDirection(iA, iB) {
        Vec3.sub(elements[iA].geometry.direction, elements[iB].geometry.center, elements[iA].geometry.center);
        Vec3.normalize(elements[iA].geometry.direction, elements[iA].geometry.direction);
    }
    var tmpV = Vec3.zero();
    function fixTerminalLinkDirection(iA, indexB, unitB) {
        var pos = unitB.conformation.position, geo = elements[iA].geometry;
        Vec3.sub(geo.direction, pos(unitB.elements[indexB], tmpV), geo.center);
        Vec3.normalize(geo.direction, geo.direction);
    }
    // get carbohydrate elements and carbohydrate links induced by intra-residue bonds
    for (var i = 0, il = structure.units.length; i < il; ++i) {
        var unit = structure.units[i];
        if (!Unit.isAtomic(unit))
            continue;
        var model = unit.model, rings = unit.rings;
        var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments, atoms = _a.atoms;
        var label_comp_id = atoms.label_comp_id;
        var chainIt = Segmentation.transientSegments(chainAtomSegments, unit.elements);
        var residueIt = Segmentation.transientSegments(residueAtomSegments, unit.elements);
        var sugarResidueMap = void 0;
        while (chainIt.hasNext) {
            residueIt.setSegment(chainIt.move());
            while (residueIt.hasNext) {
                var residueIndex = residueIt.move().index;
                var saccharideComp = getSaccharideComp(label_comp_id.value(residueAtomSegments.offsets[residueIndex]), model);
                if (!saccharideComp)
                    continue;
                if (!sugarResidueMap) {
                    sugarResidueMap = UnitRings.byFingerprintAndResidue(rings, SugarRingFps);
                }
                var sugarRings = filterFusedRings(rings, sugarResidueMap.get(residueIndex));
                if (!sugarRings || !sugarRings.length) {
                    partialElements.push({ unit: unit, residueIndex: residueIndex, component: saccharideComp });
                    continue;
                }
                var ringElements = [];
                for (var j = 0, jl = sugarRings.length; j < jl; ++j) {
                    var ringAtoms = rings.all[sugarRings[j]];
                    var anomericCarbon = getAnomericCarbon(unit, ringAtoms);
                    var ma = PrincipalAxes.calculateMomentsAxes(getPositions(unit, ringAtoms));
                    var center = Vec3.copy(Vec3.zero(), ma.origin);
                    var normal = Vec3.copy(Vec3.zero(), ma.dirC);
                    var direction = getDirection(Vec3.zero(), unit, anomericCarbon, center);
                    Vec3.orthogonalize(direction, normal, direction);
                    var ringAltId = UnitRing.getAltId(unit, ringAtoms);
                    var elementIndex = elements.length;
                    ringElements.push(elementIndex);
                    addRingElement(ringElementKey(residueIndex, unit.id, ringAltId), elementIndex);
                    if (ringAltId)
                        addRingElement(ringElementKey(residueIndex, unit.id, ''), elementIndex);
                    elements.push({
                        geometry: { center: center, normal: normal, direction: direction },
                        component: saccharideComp,
                        ringIndex: sugarRings[j],
                        altId: ringAltId,
                        unit: unit, residueIndex: residueIndex
                    });
                }
                // add carbohydrate links induced by intra-residue bonds
                // (e.g. for structures from the PDB archive __before__ carbohydrate remediation)
                var ringCombinations = combinations(fillSerial(new Array(sugarRings.length)), 2);
                for (var j = 0, jl = ringCombinations.length; j < jl; ++j) {
                    var rc = ringCombinations[j];
                    var r0 = rings.all[sugarRings[rc[0]]], r1 = rings.all[sugarRings[rc[1]]];
                    // 1,6 glycosidic links are distance 3 and 1,4 glycosidic links are distance 2
                    if (IntAdjacencyGraph.areVertexSetsConnected(unit.bonds, r0, r1, 3)) {
                        var re0 = ringElements[rc[0]];
                        var re1 = ringElements[rc[1]];
                        if (elements[re0].altId === elements[re1].altId) {
                            // TODO handle better, for now fix both directions as it is unclear where the C1 atom is
                            //      would need to know the path connecting the two rings
                            fixLinkDirection(re0, re1);
                            fixLinkDirection(re1, re0);
                            links.push({ carbohydrateIndexA: re0, carbohydrateIndexB: re1 });
                            links.push({ carbohydrateIndexA: re1, carbohydrateIndexB: re0 });
                        }
                    }
                }
            }
        }
    }
    function getRingElementIndices(unit, index) {
        return elementsWithRingMap.get(ringElementKey(unit.getResidueIndex(index), unit.id, getAltId(unit, index))) || [];
    }
    // add carbohydrate links induced by intra-unit bonds
    // (e.g. for structures from the PDB archive __after__ carbohydrate remediation)
    for (var i = 0, il = elements.length; i < il; ++i) {
        var cA = elements[i];
        var unit = cA.unit;
        for (var j = i + 1; j < il; ++j) {
            var cB = elements[j];
            if (unit !== cB.unit || cA.residueIndex === cB.residueIndex)
                continue;
            var rA = unit.rings.all[cA.ringIndex];
            var rB = unit.rings.all[cB.ringIndex];
            if (IntAdjacencyGraph.areVertexSetsConnected(unit.bonds, rA, rB, 3)) {
                // TODO handle better, for now fix both directions as it is unclear where the C1 atom is
                //      would need to know the path connecting the two rings
                fixLinkDirection(i, j);
                fixLinkDirection(j, i);
                links.push({ carbohydrateIndexA: i, carbohydrateIndexB: j });
                links.push({ carbohydrateIndexA: j, carbohydrateIndexB: i });
            }
        }
    }
    // get carbohydrate links induced by inter-unit bonds, that is
    // inter monosaccharide links for structures from the
    // PDB archive __before__ carbohydrate remediation
    // plus terminal links for __before__ and __after__
    for (var i = 0, il = structure.units.length; i < il; ++i) {
        var unit = structure.units[i];
        if (!Unit.isAtomic(unit))
            continue;
        structure.interUnitBonds.getConnectedUnits(unit.id).forEach(function (pairBonds) {
            pairBonds.connectedIndices.forEach(function (indexA) {
                pairBonds.getEdges(indexA).forEach(function (_a) {
                    var props = _a.props, indexB = _a.indexB;
                    if (!BondType.isCovalent(props.flag))
                        return;
                    var unitA = structure.unitMap.get(pairBonds.unitA);
                    var unitB = structure.unitMap.get(pairBonds.unitB);
                    var ringElementIndicesA = getRingElementIndices(unitA, indexA);
                    var ringElementIndicesB = getRingElementIndices(unitB, indexB);
                    if (ringElementIndicesA.length > 0 && ringElementIndicesB.length > 0) {
                        var lA = ringElementIndicesA.length;
                        var lB = ringElementIndicesB.length;
                        for (var j = 0, jl = Math.max(lA, lB); j < jl; ++j) {
                            var ringElementIndexA = ringElementIndicesA[Math.min(j, lA - 1)];
                            var ringElementIndexB = ringElementIndicesB[Math.min(j, lB - 1)];
                            var atomIdA = getAtomId(unitA, indexA);
                            if (atomIdA.startsWith('O1') || atomIdA.startsWith('C1')) {
                                fixLinkDirection(ringElementIndexA, ringElementIndexB);
                            }
                            links.push({
                                carbohydrateIndexA: ringElementIndexA,
                                carbohydrateIndexB: ringElementIndexB
                            });
                        }
                    }
                    else if (ringElementIndicesB.length === 0) {
                        for (var _i = 0, ringElementIndicesA_1 = ringElementIndicesA; _i < ringElementIndicesA_1.length; _i++) {
                            var ringElementIndexA = ringElementIndicesA_1[_i];
                            var atomIdA = getAtomId(unitA, indexA);
                            if (atomIdA.startsWith('O1') || atomIdA.startsWith('C1')) {
                                fixTerminalLinkDirection(ringElementIndexA, indexB, unitB);
                            }
                            terminalLinks.push({
                                carbohydrateIndex: ringElementIndexA,
                                elementIndex: indexB,
                                elementUnit: unitB,
                                fromCarbohydrate: true
                            });
                        }
                    }
                    else if (ringElementIndicesA.length === 0) {
                        for (var _b = 0, ringElementIndicesB_1 = ringElementIndicesB; _b < ringElementIndicesB_1.length; _b++) {
                            var ringElementIndexB = ringElementIndicesB_1[_b];
                            terminalLinks.push({
                                carbohydrateIndex: ringElementIndexB,
                                elementIndex: indexA,
                                elementUnit: unitA,
                                fromCarbohydrate: false
                            });
                        }
                    }
                });
            });
        });
    }
    return __assign({ links: links, terminalLinks: terminalLinks, elements: elements, partialElements: partialElements }, buildLookups(elements, links, terminalLinks));
}
function buildLookups(elements, links, terminalLinks) {
    function key(unit, element) {
        return cantorPairing(unit.id, element);
    }
    function getIndices(map, unit, index) {
        var indices = [];
        var il = map.get(key(unit, index));
        if (il !== undefined) {
            for (var _i = 0, il_1 = il; _i < il_1.length; _i++) {
                var i = il_1[_i];
                arraySetAdd(indices, i);
            }
        }
        return indices;
    }
    // elements
    var elementsMap = new Map();
    for (var i = 0, il = elements.length; i < il; ++i) {
        var _a = elements[i], unit = _a.unit, ringIndex = _a.ringIndex;
        var ring = unit.rings.all[ringIndex];
        for (var j = 0, jl = ring.length; j < jl; ++j) {
            var k = key(unit, unit.elements[ring[j]]);
            var e = elementsMap.get(k);
            if (e === undefined)
                elementsMap.set(k, [i]);
            else
                e.push(i);
        }
    }
    function getElementIndices(unit, index) {
        return getIndices(elementsMap, unit, index);
    }
    // links
    var linksMap = new Map();
    for (var i = 0, il = links.length; i < il; ++i) {
        var l = links[i];
        var _b = elements[l.carbohydrateIndexA], unit = _b.unit, ringIndex = _b.ringIndex;
        var ring = unit.rings.all[ringIndex];
        for (var j = 0, jl = ring.length; j < jl; ++j) {
            var k = key(unit, unit.elements[ring[j]]);
            var e = linksMap.get(k);
            if (e === undefined)
                linksMap.set(k, [i]);
            else
                e.push(i);
        }
    }
    function getLinkIndices(unit, index) {
        return getIndices(linksMap, unit, index);
    }
    // terminal links
    var terminalLinksMap = new Map();
    for (var i = 0, il = terminalLinks.length; i < il; ++i) {
        var _c = terminalLinks[i], fromCarbohydrate = _c.fromCarbohydrate, carbohydrateIndex = _c.carbohydrateIndex, elementUnit = _c.elementUnit, elementIndex = _c.elementIndex;
        if (fromCarbohydrate) {
            var _d = elements[carbohydrateIndex], unit = _d.unit, ringIndex = _d.ringIndex;
            var ring = unit.rings.all[ringIndex];
            for (var j = 0, jl = ring.length; j < jl; ++j) {
                var k = key(unit, unit.elements[ring[j]]);
                var e = terminalLinksMap.get(k);
                if (e === undefined)
                    terminalLinksMap.set(k, [i]);
                else
                    e.push(i);
            }
        }
        else {
            var k = key(elementUnit, elementUnit.elements[elementIndex]);
            var e = terminalLinksMap.get(k);
            if (e === undefined)
                terminalLinksMap.set(k, [i]);
            else
                e.push(i);
        }
    }
    function getTerminalLinkIndices(unit, index) {
        return getIndices(terminalLinksMap, unit, index);
    }
    return { getElementIndices: getElementIndices, getLinkIndices: getLinkIndices, getTerminalLinkIndices: getTerminalLinkIndices };
}
//# sourceMappingURL=compute.js.map